import React, {useState} from 'react';

import {Button, CheckIcon, Collapse, CreateIcon, ModalPopup, SearchInput} from '@kit';

import {cls} from '../../../../components/utils';
import {useCharacter} from '../character-context';
import {ExpandButton, expandOnClick} from './expandable-row';
import {byTitle} from './format';
import {SpellDescription} from './spell-description';
import {SpellTitleIcons} from './spell-title-icons';

import css from './sheet.module.css';

type Props = {
	classId: string;
};

/** вписать в книгу заклинание класса доступного круга: переписанное со свитка или из чужой книги */
export const SpellbookAdd: React.FC<Props> = ({classId}) => {
	const [isOpen, setOpen] = useState(false);

	return (
		<>
			<Button icon={<CreateIcon />} onClick={() => setOpen(true)}>
				Добавить в книгу
			</Button>
			{isOpen && <SpellbookAddModal classId={classId} onClose={() => setOpen(false)} />}
		</>
	);
};

type ModalProps = Props & {
	onClose(): void;
};

const SpellbookAddModal: React.FC<ModalProps> = ({classId, onClose}) => {
	const {catalog, character, update} = useCharacter();
	const [search, setSearch] = useState('');
	const maxLevel = character.maxSpellLevel(classId);
	const inBook = new Set(
		character.knownSpells.filter((spell) => spell.classIds.includes(classId)).map((spell) => spell.id),
	);
	const query = search.trim().toLowerCase();
	const spells = catalog.spells
		.filter((spell) => spell.level > 0 && spell.level <= maxLevel)
		.filter((spell) => spell.classIds.includes(classId) && !inBook.has(spell.id))
		.filter((spell) => !query || spell.title.toLowerCase().includes(query))
		.sort((first, second) => first.level - second.level || byTitle(first.title, second.title));
	const levels = [...new Set(spells.map((spell) => spell.level))];

	const add = (spellId: string) =>
		update((current) => ({
			spellbookSpells: {
				...current.spellbookSpells,
				[classId]: [...(current.spellbookSpells?.[classId] || []), spellId],
			},
		}));

	return (
		<ModalPopup open onClose={onClose} header="Добавить в книгу" maxWidth={640}>
			<SearchInput
				className={css.spellbookAddSearch}
				value={search}
				placeholder="Название заклинания"
				onChange={setSearch}
				autoFocus
			/>
			{!spells.length && (
				<p className={css.muted}>
					{query ? 'Ничего не найдено.' : 'Все заклинания доступных кругов уже в книге.'}
				</p>
			)}
			{levels.map((level) => (
				<div key={level}>
					<h3 className={css.groupTitle}>{level} круг</h3>
					<ul className={css.spellList}>
						{spells
							.filter((spell) => spell.level === level)
							.map((spell) => (
								<SpellbookAddRow
									key={spell.id}
									spellId={spell.id}
									title={spell.title}
									onAdd={() => add(spell.id)}
								/>
							))}
					</ul>
				</div>
			))}
			<div className={css.modalButtons}>
				<Button appearance="primary" icon={<CheckIcon />} onClick={onClose}>
					Готово
				</Button>
			</div>
		</ModalPopup>
	);
};

type RowProps = {
	spellId: string;
	title: string;
	onAdd(): void;
};

const SpellbookAddRow: React.FC<RowProps> = ({spellId, title, onAdd}) => {
	const [isOpen, setOpen] = useState(false);

	return (
		<li className={css.spellRow}>
			<div className={css.spell}>
				<ExpandButton isOpen={isOpen} onToggle={() => setOpen(!isOpen)} />
				<div className={css.spellBody}>
					<span
						className={cls(css.spellName, css.expandable)}
						onClick={expandOnClick(() => setOpen(!isOpen))}
					>
						{title}
						<SpellTitleIcons spellId={spellId} />
					</span>
					<span className={css.spellActions}>
						<Button size="small" icon={<CreateIcon />} onClick={onAdd}>
							Добавить
						</Button>
					</span>
				</div>
			</div>
			<Collapse open={isOpen}>
				<SpellDescription spellId={spellId} />
			</Collapse>
		</li>
	);
};
